import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';

import { Routes } from 'config';
import { useLoggerHook } from 'hooks';
import { Loader } from 'components/loader';

// Eager load layouts (needed immediately)
import RootLayout from './layout';
import PrivateLayout from './(auth)/layout';

// Lazy load pages
const NotFound = lazy(() => import('./(public)/not-found'));
const LoginPage = lazy(() => import('./(public)/login'));
const AuthCallbackPage = lazy(() => import('./(public)/auth-callback'));
const SsoCallbackPage = lazy(() => import('./(public)/sso-callback'));
const SsoIdpStartLoginPage = lazy(
  () => import('./(public)/sso-idp-start-login')
);
const VerifyPage = lazy(() => import('./(public)/verify'));
const SignUpPage = lazy(() => import('./(public)/sign-up'));
const SelectVerifyPage = lazy(() => import('./(public)/select-verify'));
const ForgotPasswordPage = lazy(() => import('./(public)/forgot-password'));
const DashboardPage = lazy(() => import('./(auth)/dashboard'));
const ResourcesPage = lazy(() => import('./(auth)/resources'));
const ResourceDetailsPage = lazy(() => import('./(auth)/resources/details'));
const CourseFormPage = lazy(() => import('./(auth)/forms/details'));
const AdditionalInfoPage = lazy(() => import('./(auth)/additional-info'));
const HomePage = lazy(() => import('./home'));
const GetStartedPage = lazy(() => import('./(auth)/get-started'));
const ProfilePage = lazy(() => import('./(auth)/profile'));

const toChildPath = (path: string) => path.replace(/^\//, '');

const publicRoutes: RouteObject[] = [
  {
    element: <LoginPage />,
    path: toChildPath(Routes.Auth.Login),
  },
  {
    element: <SignUpPage />,
    path: toChildPath(Routes.Auth.Signup),
  },
  {
    element: <SelectVerifyPage />,
    path: toChildPath(Routes.Auth.SelectVerify),
  },
  {
    element: <VerifyPage />,
    path: toChildPath(Routes.Auth.Verify),
  },
  {
    element: <ForgotPasswordPage />,
    path: toChildPath(Routes.Auth.ForgotPassword),
  },
  {
    element: <AuthCallbackPage />,
    path: toChildPath(Routes.Auth.Callback),
  },
  {
    element: <SsoCallbackPage />,
    path: toChildPath(Routes.Auth.SsoCallback),
  },
  {
    element: <SsoIdpStartLoginPage />,
    path: toChildPath(Routes.Auth.SsoIdpStartLogin),
  },
];

const privateRoutes: RouteObject[] = [
  {
    element: <DashboardPage />,
    path: toChildPath(Routes.Dashboard.Home),
  },
  {
    element: <ResourcesPage />,
    path: toChildPath(Routes.Resources.Home),
  },
  {
    element: <ResourceDetailsPage />,
    path: toChildPath(Routes.Course.Resource),
  },
  {
    element: <CourseFormPage />,
    path: toChildPath(Routes.Course.Form),
  },
  {
    element: <AdditionalInfoPage />,
    path: toChildPath(Routes.AdditionalInfo.Home),
  },
  {
    element: <GetStartedPage />,
    path: toChildPath(Routes.GetStarted.Home),
  },
  {
    element: <ProfilePage />,
    path: toChildPath(Routes.Profile.Home),
  },
];

export const appRoutes: RouteObject[] = [
  {
    path: Routes.Home,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      ...publicRoutes,
      {
        children: privateRoutes,
        element: <PrivateLayout />,
      },
      {
        path: Routes.NotFound,
        element: <NotFound homePath={Routes.Home} />,
      },
      {
        path: '*',
        element: <Navigate replace to={Routes.NotFound} />,
      },
    ],
  },
];

export const AppRoutes = () => {
  const element = useRoutes(appRoutes);

  useLoggerHook();

  return <Suspense fallback={<Loader isLoading />}>{element}</Suspense>;
};
